import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Wifi, WifiOff } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function UserActivityHandler(): React.JSX.Element | null {
  const { user } = useAuth();
  const authenticated = !!user;
  const currentUser = user && typeof user === "object" ? user : null;
  const [available, setAvailable] = useState(false);
  const [hasListing, setHasListing] = useState(false);
  const [busy, setBusy] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastPingRef = useRef(Date.now());

  useEffect(() => {
    if (user) setAvailable(!!user.is_available);
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    if (!authenticated || !currentUser || currentUser.role === "admin" || currentUser.advertiser_status !== "approved") {
      setHasListing(false);
      return undefined;
    }
    api
      .get("/listings/me")
      .then(({ data }) => {
        if (!cancelled) setHasListing(Array.isArray(data) && data.length > 0);
      })
      .catch(() => {
        if (!cancelled) setHasListing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [authenticated, currentUser?.id, currentUser?.role, currentUser?.advertiser_status]);

  const updateAvailability = useCallback(
    async (val: boolean) => {
      if (busy) return;
      setBusy(true);
      try {
        await api.post("/user/availability");
        setAvailable(val);
        if (val) {
          toast.success("Você está online — visível em filtros e cards.");
        } else {
          toast.info("Você ficou offline.");
        }
      } catch {
        toast.error("Não foi possível atualizar seu status agora.");
      } finally {
        setBusy(false);
      }
    },
    [busy]
  );

  const handleInactivity = useCallback(() => {
    if (available) updateAvailability(false);
  }, [available, updateAvailability]);

  const resetTimer = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(handleInactivity, 5 * 60 * 1000);
    const now = Date.now();
    if (now - lastPingRef.current > 2 * 60 * 1000) {
      api.get("/auth/me").catch(() => {});
      lastPingRef.current = now;
    }
  }, [handleInactivity]);

  useEffect(() => {
    if (!authenticated || !available) return undefined;
    const passiveEvents = ["scroll", "touchstart"];
    const activeEvents = ["mousemove", "keydown", "click"];
    passiveEvents.forEach((e) => window.addEventListener(e, resetTimer, { passive: true }));
    activeEvents.forEach((e) => window.addEventListener(e, resetTimer));
    resetTimer();
    return () => {
      passiveEvents.forEach((e) => window.removeEventListener(e, resetTimer));
      activeEvents.forEach((e) => window.removeEventListener(e, resetTimer));
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [authenticated, available, resetTimer]);

  if (!authenticated || user?.role === "admin") return null;
  if (user?.advertiser_status !== "approved" || !hasListing) return null;

  const Icon = available ? Wifi : WifiOff;
  const label = available ? "Ficar Offline" : "Ficar Online";
  const bg = available ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700";
  const iconColor = "text-white";

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-40"
      style={{ top: "calc(4rem + 0.55rem)" }}
      data-testid="availability-toggle-wrapper"
    >
      <button
        type="button"
        onClick={() => updateAvailability(!available)}
        disabled={busy}
        title={available ? "Você está online. Clique para ficar offline." : "Você está offline. Clique para ficar online."}
        aria-label={available ? "Ficar Offline" : "Ficar Online"}
        aria-pressed={available}
        data-testid="availability-toggle"
        className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full border text-sm font-bold whitespace-nowrap shadow-lg transition-all duration-200 ${bg} border-transparent ${iconColor} disabled:opacity-60`}
      >
        <Icon className="w-4 h-4" />
        {label}
      </button>
    </div>
  );
}
